/**
 * Configuration endpoints for Mews Booking Engine API
 * 
 * Implements:
 * - getConfiguration() - Get booking engine configuration
 * - getHotels() - Get hotel and room metadata
 * - getPaymentConfiguration() - Get payment configuration
 */

import { getApiClient } from './client';
import type {
  ConfigurationResponse,
  HotelsResponse,
  PaymentConfigurationResponse,
  ApiResponse,
} from './types';

/**
 * Cache for configuration data
 */
interface ConfigurationCache {
  configuration?: {
    data: ConfigurationResponse;
    timestamp: number;
  };
  hotels?: {
    data: HotelsResponse;
    timestamp: number;
  };
  paymentConfiguration?: {
    data: PaymentConfigurationResponse;
    timestamp: number;
  };
}

const cache: ConfigurationCache = {};
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Checks if cached data is still valid
 */
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION_MS;
}

/**
 * Gets configuration data for the booking engine
 * 
 * Includes age categories, policies, check-in/out times, currency settings, etc.
 * 
 * @param useCache - Whether to use cached data if available (default: true)
 * @returns Promise resolving to configuration response
 * 
 * @example
 * ```typescript
 * const response = await getConfiguration();
 * if (response.data) {
 *   const ageCategories = response.data.AgeCategories;
 *   const policies = response.data.Policies;
 * }
 * ```
 */
export async function getConfiguration(
  useCache: boolean = true
): Promise<ApiResponse<ConfigurationResponse>> {
  const client = getApiClient();

  // Check cache first
  if (useCache && cache.configuration && isCacheValid(cache.configuration.timestamp)) {
    return {
      data: cache.configuration.data,
      status: 200,
    };
  }

  // Make API request - Configuration endpoint requires POST with JSON body
  const configurationId = client.getConfigurationId();
  
  if (!configurationId) {
    return {
      error: {
        Message: 'Configuration ID is required. Please set VITE_MEWS_CONFIGURATION_ID environment variable.',
        Code: 'MISSING_CONFIGURATION_ID',
      },
      status: 400,
    };
  }

  // According to Mews API docs: POST /api/distributor/v1/configuration/get
  // Requires: Client, Ids (array), PrimaryId
  // Note: Client identifier comes from X-Client-Identifier header, but also needed in body
  const clientIdentifier = client.getClientIdentifier();
  const requestBody = {
    Client: clientIdentifier,
    Ids: [configurationId],
    PrimaryId: configurationId,
  };

  // Log request details for debugging
  if (import.meta.env.DEV) {
    console.log('[Configuration] Request details:', {
      endpoint: '/api/distributor/v1/configuration/get',
      configurationId,
      clientIdentifier,
      requestBody,
    });
  }

  const response = await client.post<ConfigurationResponse>(
    '/api/distributor/v1/configuration/get',
    requestBody
  );

  // Log response for debugging
  if (import.meta.env.DEV) {
    console.log('[Configuration] Response:', {
      success: !response.error,
      status: response.status,
      error: response.error,
      hasData: !!response.data,
    });
  }

  // Cache successful responses
  if (response.data && useCache) {
    cache.configuration = {
      data: response.data,
      timestamp: Date.now(),
    };
  }

  return response;
}

/**
 * Gets hotel data including room type metadata and property details
 * 
 * @param hotelId - Optional hotel ID (uses environment variable if not provided)
 * @param useCache - Whether to use cached data if available (default: true)
 * @returns Promise resolving to hotels response
 * 
 * @example
 * ```typescript
 * const response = await getHotels();
 * if (response.data) {
 *   const roomCategories = response.data.RoomCategories;
 *   const hotelName = response.data.Name['en-US']; // Localized name
 *   const products = response.data.Products;
 * }
 * ```
 */
export async function getHotels(
  hotelId?: string,
  useCache: boolean = true
): Promise<ApiResponse<HotelsResponse>> {
  const client = getApiClient();
  const targetHotelId = hotelId || client.getHotelId();

  if (!targetHotelId) {
    return {
      error: {
        Message: 'Hotel ID is required. Please provide hotelId parameter or set VITE_MEWS_HOTEL_ID environment variable.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  // Check cache first
  if (useCache && cache.hotels && isCacheValid(cache.hotels.timestamp)) {
    return {
      data: cache.hotels.data,
      status: 200,
    };
  }

  // Make API request - Hotels endpoint requires POST with JSON body
  // According to Mews API docs: POST /api/distributor/v1/hotels/get
  // Requires: Client, HotelId
  const requestBody = {
    Client: client.getClientIdentifier(),
    HotelId: targetHotelId,
  };

  // Log request details for debugging
  if (import.meta.env.DEV) {
    console.log('[Hotels] Request details:', {
      endpoint: '/api/distributor/v1/hotels/get',
      hotelId: targetHotelId,
      clientIdentifier: client.getClientIdentifier(),
      requestBody,
    });
  }

  const response = await client.post<HotelsResponse>(
    '/api/distributor/v1/hotels/get',
    requestBody
  );

  // Log response for debugging
  if (import.meta.env.DEV) {
    console.log('[Hotels] Response:', {
      success: !response.error,
      status: response.status,
      error: response.error,
      hasData: !!response.data,
    });
  }

  // Cache successful responses
  if (response.data && useCache) {
    cache.hotels = {
      data: response.data,
      timestamp: Date.now(),
    };
  }

  return response;
}

/**
 * Gets payment configuration for the specified hotel
 * 
 * Includes payment gateway information, surcharge configuration, etc.
 * 
 * @param hotelId - Optional hotel ID (uses environment variable if not provided)
 * @param useCache - Whether to use cached data if available (default: true)
 * @returns Promise resolving to payment configuration response
 * 
 * @example
 * ```typescript
 * const response = await getPaymentConfiguration();
 * if (response.data) {
 *   const gateway = response.data.PaymentGateway;
 *   const surchargeConfig = response.data.SurchargeConfiguration;
 * }
 * ```
 */
export async function getPaymentConfiguration(
  hotelId?: string,
  useCache: boolean = true
): Promise<ApiResponse<PaymentConfigurationResponse>> {
  const client = getApiClient();
  const targetHotelId = hotelId || client.getHotelId();

  if (!targetHotelId) {
    return {
      error: {
        Message: 'Hotel ID is required. Please provide hotelId parameter or set VITE_MEWS_HOTEL_ID environment variable.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  // Check cache first
  if (useCache && cache.paymentConfiguration && isCacheValid(cache.paymentConfiguration.timestamp)) {
    return {
      data: cache.paymentConfiguration.data,
      status: 200,
    };
  }

  // Make API request - Payment Configuration endpoint requires POST with JSON body
  // According to Mews API docs: POST /api/distributor/v1/hotels/getPaymentConfiguration
  // Requires: Client, HotelId
  const requestBody = {
    Client: client.getClientIdentifier(),
    HotelId: targetHotelId,
  };

  // Log request details for debugging
  if (import.meta.env.DEV) {
    console.log('[Payment Configuration] Request details:', {
      endpoint: '/api/distributor/v1/hotels/getPaymentConfiguration',
      hotelId: targetHotelId,
      clientIdentifier: client.getClientIdentifier(),
      requestBody,
    });
  }

  const response = await client.post<PaymentConfigurationResponse>(
    '/api/distributor/v1/hotels/getPaymentConfiguration',
    requestBody
  );

  // Log response for debugging
  if (import.meta.env.DEV) {
    console.log('[Payment Configuration] Response:', {
      success: !response.error,
      status: response.status,
      error: response.error,
      hasData: !!response.data,
    });
  }

  // Cache successful responses
  if (response.data && useCache) {
    cache.paymentConfiguration = {
      data: response.data,
      timestamp: Date.now(),
    };
  }

  return response;
}

/**
 * Clears all cached configuration data
 * Useful for testing or forcing fresh data
 */
export function clearConfigurationCache(): void {
  cache.configuration = undefined;
  cache.hotels = undefined;
  cache.paymentConfiguration = undefined;
}

/**
 * Returns true only if the hotel supports Option B (Payment Card Authorization via PCI Proxy).
 * Option B requires PaymentGateway to be present and PaymentCardStorageType === 'PciProxy'.
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 3
 * @see paymentConfig.json - PaymentGateway.PaymentCardStorageType
 */
export function canUseOptionB(
  paymentConfig: PaymentConfigurationResponse | null | undefined
): boolean {
  return (
    paymentConfig?.PaymentGateway != null &&
    paymentConfig.PaymentGateway.PaymentCardStorageType === 'PciProxy'
  );
}
